import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { publishErrorMessage } from '@/lib/quickPublish'
import {
  CalendarClock, Upload, Loader2, Monitor, Users, Check, Plus, X, Search, ChevronLeft, Zap,
} from 'lucide-react'
import { useIsMobile } from '@meno/ui/hooks/use-mobile'
import { cn } from '@meno/ui/lib/utils'
import { Button } from '@meno/ui/components/ui/button'
import { Badge } from '@meno/ui/components/ui/badge'
import { Input } from '@meno/ui/components/ui/input'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@meno/ui/components/ui/dialog'
import { getScreens, getScreenGroups, createScreenGroup } from '@meno/ui/lib/api'

type ScreenItem = { id: number; name: string; status?: string }
// online_count/total_count ride on ScreenGroupOut already (see
// schemas/screen_group.py) — reused here instead of a separate fetch to know
// how many of a selected group's screens are actually reachable right now.
type GroupItem  = { id: number; name: string; online_count?: number; total_count?: number }

export type PublishPayload = {
  screen_ids: number[]
  group_ids: number[]
  start_at: string | null
  end_at: string | null
}

// Publish / Schedule dialog shared by every content kind (playlist, layout,
// media, audio, link/menu, stop screen). It does BOTH actions in one place:
// by default it publishes now, and a "Schedule for later" checkbox reveals the
// window inputs. A "Start time only" checkbox drops the end bound so the
// content turns on at start_at and stays until re-published (end_at = null).
//
// `kind` only switches a handful of toast / heading strings. It does NOT change
// any logic. `mode` is optional and only seeds the initial state of the
// schedule toggle (so a dedicated "Schedule" button can open straight into the
// scheduling UI); callers that just want "Publish" can omit it.
export function PublishDialog<TResp>({
  open, mode = 'publish', kind, publish, onClose, onPublished,
}: {
  open: boolean
  mode?: 'publish' | 'schedule'
  kind: 'playlist' | 'layout' | 'audio' | 'media' | 'menu' | 'stop_screen' | 'transition' | 'mix'
  publish: (payload: PublishPayload) => Promise<{ data: TResp }>
  onClose: () => void
  onPublished: (updated: TResp) => void
}) {
  const { t } = useTranslation()

  // Defaults: start now (rounded to the next full hour), end +1 hour after.
  const defaults = useMemo(() => {
    const pad = (n: number) => String(n).padStart(2, '0')
    const fmtDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1, 0, 0)
    const end = new Date(start.getTime() + 60 * 60 * 1000)
    return {
      startDate: fmtDate(start), startTime: `${pad(start.getHours())}:00`,
      endDate:   fmtDate(end),   endTime:   `${pad(end.getHours())}:00`,
    }
  }, [open])

  // `schedule` is the effective mode toggle; `startOnly` drops the end bound.
  const [schedule,  setSchedule]  = useState(mode === 'schedule')
  const [startOnly, setStartOnly] = useState(false)
  const [startDate, setStartDate] = useState(defaults.startDate)
  const [startTime, setStartTime] = useState(defaults.startTime)
  const [endDate,   setEndDate]   = useState(defaults.endDate)
  const [endTime,   setEndTime]   = useState(defaults.endTime)
  const [tab, setTab] = useState<'screens' | 'groups'>('screens')
  const [screens, setScreens] = useState<ScreenItem[]>([])
  const [groups,  setGroups]  = useState<GroupItem[]>([])
  const [selectedScreens, setSelectedScreens] = useState<Set<number>>(new Set())
  const [selectedGroups,  setSelectedGroups]  = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // Inline group creation (Groups tab).
  const [creatingGroup, setCreatingGroup] = useState(false)
  const [newGroupName,  setNewGroupName]  = useState('')
  const [savingGroup,   setSavingGroup]   = useState(false)
  // Phone: a full-screen, two-step flow (Screens → When) instead of one
  // cramped form. Desktop keeps the single dialog.
  const isMobile = useIsMobile()
  const [step, setStep] = useState<'targets' | 'when'>('targets')
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!open) return
    setSchedule(mode === 'schedule')
    setStartOnly(false)
    setStartDate(defaults.startDate); setStartTime(defaults.startTime)
    setEndDate(defaults.endDate);     setEndTime(defaults.endTime)
    setSelectedScreens(new Set())
    setSelectedGroups(new Set())
    setTab('screens')
    setCreatingGroup(false)
    setNewGroupName('')
    setStep('targets')
    setQuery('')
    setLoading(true)
    Promise.all([getScreens(), getScreenGroups()])
      .then(([s, g]) => {
        setScreens((s.data || []).map((x: { id: number; name: string; status?: string }) => ({ id: x.id, name: x.name, status: x.status })))
        setGroups((g.data || []).map((x: { id: number; name: string; online_count?: number; total_count?: number }) => (
          { id: x.id, name: x.name, online_count: x.online_count, total_count: x.total_count }
        )))
      })
      .catch(() => toast.error(t('playlist.scheduleLoadFailed', 'Could not load screens and groups.')))
      .finally(() => setLoading(false))
  }, [open, defaults, mode, t])

  const toggleScreen = (id: number) => {
    setSelectedScreens(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  const toggleGroup = (id: number) => {
    setSelectedGroups(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  // Create an empty group inline and auto-select it, so the operator never
  // leaves the publish flow to spin up a new target bundle.
  const submitNewGroup = async () => {
    const name = newGroupName.trim()
    if (!name || savingGroup) return
    setSavingGroup(true)
    try {
      const { data } = await createScreenGroup({ name, screen_ids: [] })
      const g = { id: data.id as number, name: data.name as string }
      setGroups(prev => [g, ...prev])
      setSelectedGroups(prev => new Set(prev).add(g.id))
      setCreatingGroup(false)
      setNewGroupName('')
      toast.success(t('group.created', 'Group created'))
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || t('common.error', 'Something went wrong'))
    } finally {
      setSavingGroup(false)
    }
  }

  // Local date+time → UTC ISO for the backend window bounds.
  const toIso = (date: string, time: string) => new Date(`${date}T${time}`).toISOString()
  // A scheduled publish always needs a start; the end is only required when
  // "Start time only" is off. Publish-now sends no window at all.
  const needEnd  = schedule && !startOnly
  const startMs  = schedule ? new Date(`${startDate}T${startTime}`).getTime() : 0
  const endMs    = needEnd  ? new Date(`${endDate}T${endTime}`).getTime()     : 0
  const windowInvalid = schedule && (
    !startDate || !startTime || (needEnd && (!endDate || !endTime || endMs <= startMs))
  )

  const totalSelected = selectedScreens.size + selectedGroups.size
  const canSubmit = totalSelected > 0 && !windowInvalid && !submitting

  // How many of the SELECTED targets are currently offline — a group counts
  // via its own online_count/total_count (no per-member screen list carried
  // here) rather than a separate fetch. Only meaningful for "publish now":
  // a scheduled publish's window hasn't started yet, so today's connectivity
  // says nothing about whether the screen will be online when it does.
  const offlineTargetCount = () => {
    let n = 0
    for (const s of screens) if (selectedScreens.has(s.id) && s.status !== 'online') n++
    for (const g of groups) if (selectedGroups.has(g.id)) {
      n += Math.max(0, (g.total_count ?? 0) - (g.online_count ?? 0))
    }
    return n
  }

  const submit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      const { data } = await publish({
        screen_ids: Array.from(selectedScreens),
        group_ids:  Array.from(selectedGroups),
        start_at: schedule ? toIso(startDate, startTime) : null,
        end_at:   needEnd  ? toIso(endDate, endTime)     : null,
      })
      const successMsg = schedule
        ? (kind === 'layout'
            ? t('layout.scheduledOk', 'Layout scheduled')
            : kind === 'stop_screen'
              ? t('stopScreen.scheduledOk', 'Stop screen scheduled')
              : kind === 'mix'
                ? t('mix.scheduledOk', 'Mix scheduled')
                : t('playlist.scheduledOk', 'Playlist scheduled'))
        : (kind === 'mix'
            ? t('mix.publishedOk', 'Mix published')
            : kind === 'layout'
            ? t('layout.publishedOk', 'Layout published')
            : kind === 'audio'
              ? t('audioHub.streamedOk', 'Audio streaming')
              : kind === 'media'
                ? t('assets.publishedOk', 'Content published')
                : kind === 'menu'
                  ? t('link.publishedOk', 'Webpage published')
                  : kind === 'stop_screen'
                    ? t('stopScreen.publishedOk', 'Stop screen published')
                    : kind === 'transition'
                      ? t('transitions.appliedOk', 'Transition applied')
                      : t('playlist.publishedOk', 'Playlist published'))
      const offlineCount = schedule ? 0 : offlineTargetCount()
      if (offlineCount > 0) {
        // Not a plain success — a real portion of the audience won't see this
        // until they reconnect, so the toast has to say so instead of reading
        // as "done everywhere" (mirrors ScreenDetailsPage's was_offline toast).
        toast.warning(`${successMsg} — ${t('publish.offlineNote', {
          defaultValue: '{{count}} screen(s) offline, will apply once reconnected',
          count: offlineCount,
        })}`)
      } else {
        toast.success(successMsg)
      }
      onPublished(data)
    } catch (err) {
      // publishErrorMessage gives the burst guard its own wording (with the
      // wait attached) instead of "Failed to publish" — the operator hasn't
      // failed at anything, they're just going too fast.
      toast.error(publishErrorMessage(err, t as never))
    } finally {
      setSubmitting(false)
    }
  }

  const title = schedule
    ? (kind === 'layout'
        ? t('layout.scheduleTitle', 'Schedule publish')
        : t('playlist.scheduleTitle', 'Schedule publish'))
    : (kind === 'layout'
        ? t('layout.publishTitle', 'Publish layout')
        : kind === 'audio'
          ? t('audioHub.streamTitle', 'Stream audio')
          : kind === 'media'
            ? t('assets.publishTitle', 'Publish content')
            : kind === 'menu'
              ? t('link.publishTitle', 'Publish webpage')
              : kind === 'stop_screen'
                ? t('stopScreen.publishTitle', 'Publish stop screen')
                : kind === 'transition'
                  ? t('transitions.applyTitle', 'Apply transition')
                  : t('playlist.publishTitle', 'Publish playlist'))
  const description = schedule
    ? (kind === 'layout'
        ? t('layout.scheduleDescription', 'Choose when to publish and the screens or groups that should receive this layout.')
        : t('playlist.scheduleDescription', 'Choose when to publish and the screens or groups that should receive this playlist.'))
    : (kind === 'layout'
        ? t('layout.publishDescription', 'Pick the screens or groups that should play this layout now.')
        : kind === 'audio'
          ? t('audioHub.streamDescription', 'Pick the screens or groups that should stream this audio now. It overrides any audio currently playing there.')
          : kind === 'media'
            ? t('assets.publishDescription', 'Pick the screens or groups that should show this asset now. It replaces whatever content is currently playing there.')
            : kind === 'menu'
              ? t('link.publishDescription', 'Pick the screens or groups that should show this webpage now. It replaces whatever content is currently playing there.')
              : kind === 'stop_screen'
                ? t('stopScreen.publishDescription', 'Pick the screens or groups that should show this stop screen while idle. It layers on top of their current content.')
                : kind === 'transition'
                  ? t('transitions.applyDescription', 'Pick the screens or groups that should use this transition between content items.')
                  : t('playlist.publishDescription', 'Pick the screens or groups that should play this playlist now.'))

  const q = query.trim().toLowerCase()
  const shownScreens = q ? screens.filter(x => x.name.toLowerCase().includes(q)) : screens
  const shownGroups  = q ? groups.filter(x => x.name.toLowerCase().includes(q)) : groups
  const submitLabel = schedule
    ? t('playlist.schedule', 'Schedule')
    : kind === 'transition' ? t('transitions.apply', 'Apply') : t('playlist.publish', 'Publish')

  // ── Pieces shared by the desktop dialog and the phone flow ────────────────
  const scheduleToggle = kind !== 'transition' && (
    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
      <input
        type="checkbox"
        checked={schedule}
        onChange={e => setSchedule(e.target.checked)}
        className="w-4 h-4 rounded border-border accent-primary"
      />
      <CalendarClock className="w-3.5 h-3.5 text-muted-foreground" />
      <span>{t('publish.scheduleForLater', 'Schedule for later')}</span>
    </label>
  )

  // Single-occupant channel model: publishing now takes over the whole
  // channel; a scheduled publish only takes over its own time window.
  const overwriteHint = kind !== 'transition' && (
    <p className="text-xs text-muted-foreground">
      {schedule
        ? t('publish.overwriteHintScheduled', 'This takes over the channel only during the scheduled window; outside it, the current content keeps playing.')
        : t('publish.overwriteHintNow', 'Publishing now replaces whatever this channel is currently playing on the selected screens.')}
    </p>
  )

  // `phone`: one field per row at finger height. Side by side, a native date
  // or time input got ~140px on a phone — too narrow to read or tap.
  const scheduleFields = (phone: boolean) => {
    const field = cn('w-full min-w-0 rounded-md border border-border bg-background px-2', phone ? 'h-12 text-base' : 'h-9 text-sm')
    const row = phone ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-2 gap-3'
    return (
      <div className={phone ? 'space-y-4' : 'space-y-2'}>
        <div className={row}>
          <label className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">{t('playlist.scheduleStartDate', 'Start date')}</span>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={field} />
          </label>
          <label className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">{t('playlist.scheduleStartTime', 'Start time')}</span>
            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className={field} />
          </label>
        </div>

        {/* Start-only — drop the end bound entirely (open-ended window). */}
        <label className={cn('flex items-center gap-2 cursor-pointer select-none text-muted-foreground', phone ? 'min-h-11 text-sm' : 'text-xs')}>
          <input
            type="checkbox"
            checked={startOnly}
            onChange={e => setStartOnly(e.target.checked)}
            className={phone ? 'w-5 h-5 rounded border-border accent-primary' : 'w-3.5 h-3.5 rounded border-border accent-primary'}
          />
          <span>{t('publish.startOnly', 'Start time only (no end time)')}</span>
        </label>

        {!startOnly && (
          <div className={row}>
            <label className="flex flex-col gap-1 min-w-0">
              <span className="text-xs font-medium text-muted-foreground">{t('playlist.scheduleEndDate', 'End date')}</span>
              <input type="date" value={endDate} min={startDate || undefined} onChange={e => setEndDate(e.target.value)} className={field} />
            </label>
            <label className="flex flex-col gap-1 min-w-0">
              <span className="text-xs font-medium text-muted-foreground">{t('playlist.scheduleEndTime', 'End time')}</span>
              <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className={field} />
            </label>
          </div>
        )}
        {windowInvalid && (
          <p className="text-[11px] text-destructive">
            {t('playlist.scheduleWindowInvalid', 'End must be after start.')}
          </p>
        )}
      </div>
    )
  }

  const checkbox = (checked: boolean, phone: boolean) => (
    <span className={cn(
      'rounded border flex items-center justify-center shrink-0',
      phone ? 'w-5 h-5' : 'w-4 h-4',
      checked ? 'bg-primary border-primary text-primary-foreground' : 'border-border',
    )}>
      {checked && <Check className={phone ? 'w-3.5 h-3.5' : 'w-3 h-3'} />}
    </span>
  )

  // Screens / groups picker. On a phone the rows are 48px, each screen shows
  // whether it is online right now, and the list takes the remaining height.
  const targets = (phone: boolean) => (
    <div className={phone ? 'flex min-h-0 flex-1 flex-col' : 'mt-2'}>
      <div className={cn('inline-flex rounded-md border border-border bg-muted/40 p-0.5 mb-2', phone && 'w-full')}>
        {(['screens', 'groups'] as const).map(key => {
          const count = key === 'screens' ? selectedScreens.size : selectedGroups.size
          const Icon = key === 'screens' ? Monitor : Users
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={cn(
                'text-xs font-medium rounded inline-flex items-center justify-center gap-1.5 transition-colors',
                phone ? 'h-10 flex-1 px-3 text-sm' : 'px-3 h-7',
                tab === key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {key === 'screens' ? t('playlist.scheduleScreens', 'Screens') : t('playlist.scheduleGroups', 'Groups')}
              {count > 0 && <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{count}</Badge>}
            </button>
          )
        })}
      </div>

      {phone && (
        <div className="relative mb-2">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('common.search', 'Search')}
            className="h-11 ps-9 text-base"
          />
        </div>
      )}

      <div className={cn('overflow-y-auto rounded-md border border-border bg-card', phone ? 'min-h-0 flex-1' : 'max-h-56')}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : tab === 'screens' ? (
          shownScreens.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">
              {t('playlist.scheduleNoScreens', 'No screens available.')}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {shownScreens.map(x => {
                const checked = selectedScreens.has(x.id)
                const online = x.status === 'online'
                return (
                  <li key={x.id}>
                    <button
                      type="button"
                      onClick={() => toggleScreen(x.id)}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 text-sm text-start transition-colors',
                        phone ? 'min-h-12 py-2' : 'py-2',
                        checked ? 'bg-muted text-foreground' : 'hover:bg-muted/40',
                      )}
                    >
                      {checkbox(checked, phone)}
                      <Monitor className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate flex-1">{x.name}</span>
                      {phone && (
                        <span className={cn('flex items-center gap-1 text-[11px] shrink-0', online ? 'text-emerald-500' : 'text-muted-foreground')}>
                          <span className={cn('h-2 w-2 rounded-full', online ? 'bg-emerald-500' : 'bg-muted-foreground/50')} />
                          {online ? t('screens.online', 'Online') : t('screens.offline', 'Offline')}
                        </span>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          )
        ) : (
          <div>
            {/* Inline "New group" creator — always available at the top of
                the Groups list so a target bundle can be spun up in-flow. */}
            <div className="px-2 py-2 border-b border-border">
              {creatingGroup ? (
                <div className="flex items-center gap-1.5">
                  <Input
                    autoFocus
                    value={newGroupName}
                    onChange={e => setNewGroupName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitNewGroup(); if (e.key === 'Escape') setCreatingGroup(false) }}
                    placeholder={t('group.placeholder', 'e.g. Riyadh Lobby Screens')}
                    className={phone ? 'h-11 text-base' : 'h-8 text-sm'}
                  />
                  <Button size="icon" className={phone ? 'h-11 w-11 shrink-0' : 'h-8 w-8 shrink-0'} disabled={!newGroupName.trim() || savingGroup} onClick={submitNewGroup}>
                    {savingGroup ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  </Button>
                  <Button size="icon" variant="ghost" className={phone ? 'h-11 w-11 shrink-0' : 'h-8 w-8 shrink-0'} onClick={() => { setCreatingGroup(false); setNewGroupName('') }}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setCreatingGroup(true)}
                  className={cn('w-full flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors', phone && 'min-h-10 text-sm')}
                >
                  <Plus className="w-3.5 h-3.5" />
                  {t('group.newInline', 'New group')}
                </button>
              )}
            </div>
            {shownGroups.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">
                {t('playlist.scheduleNoGroups', 'No groups available.')}
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {shownGroups.map(g => {
                  const checked = selectedGroups.has(g.id)
                  return (
                    <li key={g.id}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(g.id)}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 text-sm text-start transition-colors',
                          phone ? 'min-h-12 py-2' : 'py-2',
                          checked ? 'bg-muted text-foreground' : 'hover:bg-muted/40',
                        )}
                      >
                        {checkbox(checked, phone)}
                        <Users className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate flex-1">{g.name}</span>
                        {phone && g.total_count != null && (
                          <span className="text-[11px] text-muted-foreground shrink-0 tabular-nums">
                            {t('publish.groupOnline', { defaultValue: '{{online}}/{{total}} online', online: g.online_count ?? 0, total: g.total_count })}
                          </span>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  )

  const submitIcon = submitting
    ? <Loader2 className="w-4 h-4 animate-spin" />
    : schedule ? <CalendarClock className="w-4 h-4" /> : <Upload className="w-4 h-4" />

  // ── Phone: full screen, Screens → When ─────────────────────────────────────
  if (isMobile) {
    const lastStep = step === 'when' || kind === 'transition'
    const stepCount = kind === 'transition' ? 1 : 2
    return (
      <Dialog open={open} onOpenChange={o => { if (!o) onClose() }}>
        <DialogContent className="flex h-dvh max-h-dvh w-screen max-w-none flex-col gap-3 rounded-none p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <DialogHeader className="text-start">
            <DialogTitle className="flex items-center gap-2">
              {schedule ? <CalendarClock className="w-4 h-4 text-primary" /> : <Upload className="w-4 h-4 text-primary" />}
              {title}
            </DialogTitle>
            {stepCount > 1 && (
              <DialogDescription>
                {t('publish.stepOf', { defaultValue: 'Step {{step}} of {{count}}', step: step === 'targets' ? 1 : 2, count: stepCount })}
                {' · '}
                {step === 'targets' ? t('publish.stepScreens', 'Where') : t('publish.stepWhen', 'When')}
              </DialogDescription>
            )}
          </DialogHeader>

          {step === 'targets' ? targets(true) : (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-2 gap-2">
                {([false, true] as const).map(later => (
                  <button
                    key={String(later)}
                    type="button"
                    onClick={() => setSchedule(later)}
                    className={cn(
                      'flex min-h-16 flex-col items-center justify-center gap-1 rounded-xl border text-sm font-medium',
                      schedule === later ? 'border-primary bg-primary/10 text-primary' : 'border-border',
                    )}
                  >
                    {later ? <CalendarClock className="w-5 h-5" /> : <Zap className="w-5 h-5" />}
                    {later ? t('publish.later', 'Schedule') : t('publish.now', 'Now')}
                  </button>
                ))}
              </div>
              {overwriteHint}
              {schedule && scheduleFields(true)}
            </div>
          )}

          <div className="flex shrink-0 items-center gap-2 border-t border-border pt-3">
            {step === 'when' ? (
              <Button variant="outline" className="h-12 gap-1" onClick={() => setStep('targets')}>
                <ChevronLeft className="w-4 h-4 rtl:rotate-180" />
                {t('common.back', 'Back')}
              </Button>
            ) : (
              <Button variant="outline" className="h-12" onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
            )}
            {lastStep ? (
              <Button onClick={submit} disabled={!canSubmit} className="h-12 flex-1 gap-2">
                {submitIcon}
                {totalSelected > 0
                  ? t('publish.toCount', { defaultValue: '{{action}} · {{count}} selected', action: submitLabel, count: totalSelected })
                  : submitLabel}
              </Button>
            ) : (
              <Button onClick={() => setStep('when')} disabled={totalSelected === 0} className="h-12 flex-1">
                {totalSelected > 0
                  ? t('publish.nextCount', { defaultValue: 'Next · {{count}} selected', count: totalSelected })
                  : t('publish.pickTargets', 'Pick screens or groups')}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {schedule
              ? <><CalendarClock className="w-4 h-4 text-primary" /> {title}</>
              : <><Upload className="w-4 h-4 text-primary" /> {title}</>}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {/* Schedule toggle — off = publish now, on = reveal the window inputs.
            Transitions are a per-screen preference applied immediately (no
            backend window), so the toggle is hidden for them. */}
        {scheduleToggle}
        {overwriteHint && <div className="-mt-1">{overwriteHint}</div>}
        {schedule && scheduleFields(false)}
        {targets(false)}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={submit} disabled={!canSubmit} className="gap-2">
            {submitIcon}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
